import { useMemo, useState } from "react";
import { Alert, Card, Chip, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import financeService, { PayrollDryRunResult, PayrollTripStatus } from "services/financeService";

interface PunchRow {
  id: string;
  employeeName: string;
  employeeCode: string;
  type: string;
  refId: string;
  hub: string;
  punchType: string;
  paycomPunchType: string;
  punchTimeUtc: string;
  timeZone: string;
}

/** One row per trip/shift per person in the dry-run report. */
interface GroupRow {
  id: string;
  employeeName: string;
  employeeCode: string;
  type: string;
  refId: string;
  hub: string;
  paidHours?: number;
  clockInUtc?: string;
  clockOutUtc?: string;
  unpaidBreakMinutes: number;
  punchCount: number;
}

const formatUtc = (iso?: string) => (iso ? moment.utc(iso).format("YYYY-MM-DD HH:mm") : "");

const parseTripIds = (text: string): number[] => {
  const ids = text
    .split(/[\s,;]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
    .map((token) => Number(token))
    .filter((n) => Number.isInteger(n) && n > 0);
  return Array.from(new Set(ids));
};

const statusChipColor = (status: string): "success" | "warning" | "error" | "info" | "default" => {
  if (status === "Published") return "success";
  if (status.startsWith("Eligible") || status === "Retry Pending" || status === "Queued") return "info";
  if (status.startsWith("Waiting") || status === "Partially Published" || status === "Ineligible") return "warning";
  if (status.startsWith("Failed") || status.startsWith("Locked") || status === "Not Found") return "error";
  return "default";
};

const escapeCsv = (value: string) => (/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

const downloadCsv = (headers: string[], lines: string[][], filePrefix: string) => {
  // Prepend a BOM so Excel reads UTF-8 correctly (same pattern as ReviewQueue export).
  const csv = "﻿" + [headers.map(escapeCsv).join(","), ...lines.map((l) => l.map(escapeCsv).join(","))].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filePrefix}-${moment().format("YYYYMMDD-HHmmss")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Payroll dry run: shows exactly what the payroll function app's next scheduled run
 * would lock and send to Paycom, WITHOUT writing the TimePunchLog rows that trigger
 * the real run. Optionally takes a list of trip ids and reports where each one sits
 * in the payroll pipeline instead. Read-only end to end — safe to run any time.
 */
const PayrollDryRun = () => {
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [tripIdsText, setTripIdsText] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<PayrollDryRunResult | null>(null);
  const [tripStatuses, setTripStatuses] = useState<PayrollTripStatus[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tripIds = useMemo(() => parseTripIds(tripIdsText), [tripIdsText]);
  const tripLookupMode = tripIdsText.trim().length > 0;

  const datesValid =
    (!startDate && !endDate) ||
    (!!startDate && !!endDate && startDate.isValid() && endDate.isValid() && !endDate.isBefore(startDate));

  const canRun = !isRunning && (tripLookupMode ? tripIds.length > 0 && tripIds.length <= 500 : datesValid);

  const handleRun = async () => {
    setIsRunning(true);
    setResult(null);
    setTripStatuses(null);
    setError(null);
    try {
      if (tripLookupMode) {
        setTripStatuses(await financeService.getPayrollTripStatuses(tripIds));
      } else {
        setResult(
          await financeService.runPayrollDryRun(startDate?.format("YYYY-MM-DD"), endDate?.format("YYYY-MM-DD"))
        );
      }
    } catch (e) {
      setError("Request failed. Try again, or contact support if it persists.");
    } finally {
      setIsRunning(false);
    }
  };

  // Dry-run report: one row per trip/shift per person, with hours and clock in/out.
  const groupRows: GroupRow[] = useMemo(
    () =>
      (result?.groups ?? []).map((group) => ({
        id: `${group.type === "Trip" ? "T" : "S"}${group.id}-${group.employeeCode}`,
        employeeName: group.employeeName ?? "",
        employeeCode: group.employeeCode,
        type: group.type,
        refId: `${group.type === "Trip" ? "T" : "S"}${group.id}`,
        hub: group.hub ?? "",
        paidHours: group.paidHours,
        clockInUtc: group.clockInUtc,
        clockOutUtc: group.clockOutUtc,
        unpaidBreakMinutes: group.unpaidBreakMinutes ?? 0,
        punchCount: group.punches.length,
      })),
    [result]
  );

  // Trip lookup mode: the punches the payroll logic computes for each requested trip.
  const statusPunchRows: PunchRow[] = useMemo(
    () =>
      (tripStatuses ?? []).flatMap((s) =>
        (s.punches ?? []).map((punch) => ({
          id: punch.punchId,
          employeeName: s.employeeName ?? "",
          employeeCode: s.employeeCode ?? "",
          type: "Trip",
          refId: `T${s.tripId}`,
          hub: s.hub ?? "",
          punchType: punch.punchType,
          paycomPunchType: punch.paycomPunchType,
          punchTimeUtc: punch.punchTimeUtc,
          timeZone: punch.timeZone ?? "",
        }))
      ),
    [tripStatuses]
  );

  const statusWarnings: string[] = useMemo(
    () => (tripStatuses ?? []).flatMap((s) => s.warnings ?? []),
    [tripStatuses]
  );

  const totalHours = useMemo(() => groupRows.reduce((sum, r) => sum + (r.paidHours ?? 0), 0), [groupRows]);

  const clockTimesText = (row: { clockInUtc?: string; clockOutUtc?: string; unpaidBreakMinutes?: number }) => {
    if (!row.clockInUtc && !row.clockOutUtc) return "";
    const range = `${row.clockInUtc ? formatUtc(row.clockInUtc) : "?"} → ${row.clockOutUtc ? formatUtc(row.clockOutUtc) : "?"}`;
    return (row.unpaidBreakMinutes ?? 0) > 0 ? `${range} · ${row.unpaidBreakMinutes}m break` : range;
  };

  const groupColumns: GridColDef[] = [
    { field: "employeeName", headerName: "Employee", flex: 1.4, minWidth: 160 },
    { field: "employeeCode", headerName: "EE Code", width: 100 },
    { field: "type", headerName: "Type", width: 80 },
    { field: "refId", headerName: "Trip/Shift", width: 110 },
    { field: "hub", headerName: "Hub", flex: 1, minWidth: 120 },
    {
      field: "paidHours",
      headerName: "Hours",
      width: 90,
      type: "number",
      valueFormatter: (params) => (params.value == null ? "—" : (params.value as number).toFixed(2)),
    },
    {
      field: "clockInUtc",
      headerName: "Clock In / Out (UTC)",
      flex: 1.6,
      minWidth: 250,
      sortComparator: (a, b) => (a ?? "").toString().localeCompare((b ?? "").toString()),
      renderCell: (params: GridRenderCellParams<GroupRow>) => (
        <Typography variant="caption" color="text.secondary" noWrap>
          {clockTimesText(params.row)}
        </Typography>
      ),
    },
    { field: "punchCount", headerName: "Punches", width: 90, type: "number" },
  ];

  const punchColumns: GridColDef[] = [
    { field: "employeeName", headerName: "Employee", flex: 1.4, minWidth: 160 },
    { field: "employeeCode", headerName: "EE Code", flex: 0.8, minWidth: 100 },
    { field: "type", headerName: "Type", width: 80 },
    { field: "refId", headerName: "Trip/Shift", width: 110 },
    { field: "hub", headerName: "Hub", flex: 1, minWidth: 120 },
    { field: "punchType", headerName: "Punch", width: 110 },
    { field: "paycomPunchType", headerName: "Paycom", width: 90 },
    {
      field: "punchTimeUtc",
      headerName: "Punch Time (UTC)",
      width: 170,
      valueFormatter: (params) => (params.value ? formatUtc(params.value as string) : ""),
    },
    { field: "timeZone", headerName: "Time Zone", width: 100 },
  ];

  const statusColumns: GridColDef[] = [
    { field: "tripId", headerName: "Trip", width: 110 },
    {
      field: "status",
      headerName: "Status",
      width: 220,
      renderCell: (params: GridRenderCellParams<PayrollTripStatus>) => (
        <Tooltip title={params.row.detail ?? ""}>
          <Chip size="small" color={statusChipColor(params.row.status)} label={params.row.status} />
        </Tooltip>
      ),
    },
    { field: "employeeName", headerName: "Employee", flex: 1.2, minWidth: 140 },
    { field: "employeeCode", headerName: "EE Code", width: 100 },
    { field: "hub", headerName: "Hub", flex: 1, minWidth: 110 },
    {
      field: "pickupDateTime",
      headerName: "Pickup",
      width: 150,
      valueFormatter: (params) => formatUtc(params.value as string | undefined),
    },
    {
      field: "approvedDateTime",
      headerName: "Approved",
      width: 150,
      valueFormatter: (params) => formatUtc(params.value as string | undefined),
    },
    {
      field: "paidHours",
      headerName: "Hours",
      width: 90,
      type: "number",
      valueFormatter: (params) => (params.value == null ? "—" : (params.value as number).toFixed(2)),
    },
    {
      field: "clockInUtc",
      headerName: "Clock In / Out (UTC)",
      flex: 1.4,
      minWidth: 230,
      sortComparator: (a, b) => (a ?? "").toString().localeCompare((b ?? "").toString()),
      renderCell: (params: GridRenderCellParams<PayrollTripStatus>) => (
        <Typography variant="caption" color="text.secondary" noWrap>
          {clockTimesText(params.row)}
        </Typography>
      ),
    },
    {
      field: "punchCount",
      headerName: "Punches",
      width: 90,
      type: "number",
      valueGetter: (params) => params.row.punches?.length ?? 0,
    },
    { field: "publishedCount", headerName: "Pub", width: 70, type: "number" },
    { field: "failedCount", headerName: "Fail", width: 70, type: "number" },
    { field: "queuedCount", headerName: "Que", width: 70, type: "number" },
    {
      field: "lastActivityUtc",
      headerName: "Last Activity (UTC)",
      width: 160,
      valueFormatter: (params) => formatUtc(params.value as string | undefined),
    },
    { field: "detail", headerName: "Detail", flex: 2, minWidth: 240 },
  ];

  const exportGroupsCsv = () =>
    downloadCsv(
      ["Employee", "EE Code", "Type", "Trip/Shift", "Hub", "Hours", "Clock In (UTC)", "Clock Out (UTC)", "Break Min", "Punches"],
      groupRows.map((r) => [
        r.employeeName, r.employeeCode, r.type, r.refId, r.hub,
        r.paidHours == null ? "" : r.paidHours.toFixed(2),
        formatUtc(r.clockInUtc), formatUtc(r.clockOutUtc),
        r.unpaidBreakMinutes.toString(), r.punchCount.toString(),
      ]),
      "payroll-dryrun-hours"
    );

  const exportPunchesCsv = (rows: PunchRow[], filePrefix: string) =>
    downloadCsv(
      ["Employee", "EE Code", "Type", "Trip/Shift", "Hub", "Punch", "Paycom", "Punch Time (UTC)", "Time Zone"],
      rows.map((r) => [
        r.employeeName, r.employeeCode, r.type, r.refId, r.hub, r.punchType, r.paycomPunchType,
        formatUtc(r.punchTimeUtc), r.timeZone,
      ]),
      filePrefix
    );

  const exportStatusesCsv = () =>
    downloadCsv(
      ["Trip", "Status", "Employee", "EE Code", "Hub", "Pickup", "Approved", "Hours", "Clock In (UTC)", "Clock Out (UTC)", "Break Min", "Published", "Failed", "Queued", "Last Activity (UTC)", "Detail", "Last Failure"],
      (tripStatuses ?? []).map((s) => [
        s.tripId.toString(), s.status, s.employeeName ?? "", s.employeeCode ?? "", s.hub ?? "",
        formatUtc(s.pickupDateTime), formatUtc(s.approvedDateTime),
        s.paidHours == null ? "" : s.paidHours.toFixed(2),
        formatUtc(s.clockInUtc), formatUtc(s.clockOutUtc), (s.unpaidBreakMinutes ?? 0).toString(),
        s.publishedCount.toString(), s.failedCount.toString(), s.queuedCount.toString(),
        formatUtc(s.lastActivityUtc), s.detail ?? "", s.lastFailureMessage ?? "",
      ]),
      "payroll-trip-status"
    );

  const summary = result?.summary;

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Payroll Dry Run
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Preview of everything the payroll job would send to Paycom on its next run — approved trips and
        shifts that have not been published yet. Read-only: nothing is locked, logged, or sent. Leave the
        dates blank to mirror the scheduled job&apos;s window, or set both to preview a specific range
        (trips by pickup date, shifts by shift end date). Alternatively, paste trip ids below to run the
        punch logic for those specific trips — you get each trip&apos;s pipeline status plus the exact
        time punches the logic computes for it.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} mb={2}>
        <DatePicker
          label="Start Date (optional)"
          value={startDate}
          onChange={(value) => setStartDate(value)}
          disabled={tripLookupMode}
          slotProps={{ textField: { size: "small" } }}
        />
        <DatePicker
          label="End Date (optional)"
          value={endDate}
          onChange={(value) => setEndDate(value)}
          disabled={tripLookupMode}
          slotProps={{ textField: { size: "small" } }}
        />
        <MDButton variant="contained" color="info" disabled={!canRun} onClick={handleRun}>
          {isRunning ? "Running…" : tripLookupMode ? `Check ${tripIds.length} Trip${tripIds.length === 1 ? "" : "s"}` : "Run Dry Run"}
        </MDButton>
        {result && groupRows.length > 0 && (
          <MDButton variant="outlined" color="info" onClick={exportGroupsCsv}>
            Export CSV
          </MDButton>
        )}
        {tripStatuses && tripStatuses.length > 0 && (
          <MDButton variant="outlined" color="info" onClick={exportStatusesCsv}>
            Export Statuses
          </MDButton>
        )}
        {tripStatuses && statusPunchRows.length > 0 && (
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => exportPunchesCsv(statusPunchRows, "payroll-trip-punches")}
          >
            Export Punches
          </MDButton>
        )}
      </Stack>

      <TextField
        label="Trip IDs (optional — paste a list to check specific trips)"
        placeholder="e.g. 5850422, 5850423 — commas, spaces, or one per line"
        value={tripIdsText}
        onChange={(e) => setTripIdsText(e.target.value)}
        multiline
        minRows={1}
        maxRows={4}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      />

      {!tripLookupMode && !datesValid && (
        <Alert severity="warning">Set both dates (end on or after start), or clear both.</Alert>
      )}
      {tripLookupMode && tripIds.length === 0 && (
        <Alert severity="warning">No valid trip ids found in the list.</Alert>
      )}
      {tripLookupMode && tripIds.length > 500 && (
        <Alert severity="warning">Too many trip ids — 500 max per request.</Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      {summary && (
        <MDBox mb={2}>
          <Alert severity={summary.totalPunches > 0 ? "info" : "success"} sx={{ mb: 1 }}>
            {summary.totalPunches === 0
              ? "Nothing is pending — the next payroll run would send no punches."
              : `The next payroll run would send ${summary.totalPunches} punches for ` +
                `${summary.employeeCount} employees (${summary.tripCount} trips, ${summary.shiftCount} shifts).`}
          </Alert>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`Generated ${formatUtc(result.generatedUtc)} UTC`} />
            {summary.earliestPunchUtc && (
              <Chip
                size="small"
                label={`Punches ${formatUtc(summary.earliestPunchUtc)} – ${formatUtc(summary.latestPunchUtc ?? summary.earliestPunchUtc)} UTC`}
              />
            )}
            {groupRows.length > 0 && <Chip size="small" label={`Total ${totalHours.toFixed(1)} paid hours`} />}
            <Chip size="small" label={`Approval delay ${summary.approvalHoursRequired}h`} />
            {summary.maxDaysToQuery != null && <Chip size="small" label={`Window ${summary.maxDaysToQuery} days`} />}
            {summary.duplicatesRemoved > 0 && (
              <Chip size="small" color="warning" label={`${summary.duplicatesRemoved} duplicates removed`} />
            )}
          </Stack>
        </MDBox>
      )}

      {result && result.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            {result.warnings.length} warning{result.warnings.length === 1 ? "" : "s"} (the publisher logs these too):
          </Typography>
          <MDBox component="ul" pl={2} m={0} sx={{ maxHeight: 160, overflowY: "auto" }}>
            {result.warnings.map((warning, i) => (
              <li key={i}>
                <Typography variant="caption">{warning}</Typography>
              </li>
            ))}
          </MDBox>
        </Alert>
      )}

      {result && groupRows.length > 0 && (
        <MDBox sx={{ height: 560 }}>
          <DataGrid
            rows={groupRows}
            columns={groupColumns}
            density="compact"
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
          />
        </MDBox>
      )}

      {tripStatuses && (
        <>
          <MDBox sx={{ height: 400 }} mb={2}>
            <DataGrid
              rows={tripStatuses}
              columns={statusColumns}
              getRowId={(row) => row.tripId}
              density="compact"
              disableRowSelectionOnClick
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[25, 50, 100]}
            />
          </MDBox>

          {statusWarnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                {statusWarnings.length} punch-logic warning{statusWarnings.length === 1 ? "" : "s"}:
              </Typography>
              <MDBox component="ul" pl={2} m={0} sx={{ maxHeight: 160, overflowY: "auto" }}>
                {statusWarnings.map((warning, i) => (
                  <li key={i}>
                    <Typography variant="caption">{warning}</Typography>
                  </li>
                ))}
              </MDBox>
            </Alert>
          )}

          <Typography variant="subtitle2" gutterBottom>
            Computed Time Punches — what the payroll logic produces for these trips right now
            (regardless of whether they already published)
          </Typography>
          {statusPunchRows.length > 0 ? (
            <MDBox sx={{ height: 400 }}>
              <DataGrid
                rows={statusPunchRows}
                columns={punchColumns}
                density="compact"
                disableRowSelectionOnClick
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                pageSizeOptions={[25, 50, 100]}
              />
            </MDBox>
          ) : (
            <Alert severity="info">
              No punches were produced for these trips — check the status column for why (not
              approved, no Paycom employee code, or no approved punch events on the trip).
            </Alert>
          )}
        </>
      )}
    </Card>
  );
};

export default PayrollDryRun;
